import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";
import { PUZZLE_SPINS } from "@/lib/engagement-shared";

const WeeklyLeaderboard = lazy(() =>
  import("@/components/WeeklyLeaderboard").then((m) => ({ default: m.WeeklyLeaderboard })),
);

const FAQ: FaqItem[] = [
  {
    q: "What is the Pal Stars weekly leaderboard?",
    a: "It ranks the best Palworld card game results of the last seven UTC days across Pal Stars: your highest graded pull score, Gem Mint 10 count, packs opened, Palsino jackpots and daily Pal Puzzle solves. The board refreshes on every visit and rolls forward one day at a time.",
  },
  {
    q: "How do I get on the Palworld leaderboard?",
    a: `Create a free Pal Stars account and play: open your free daily booster packs, spin Palsino and solve the daily Pal Puzzle for ${PUZZLE_SPINS} spins. Everything you do in the last seven days counts automatically — there is nothing to submit.`,
  },
  {
    q: "How is the best pull score calculated?",
    a: "Every card pulled in the pack opening game is graded server-side on centering, corners, edges and surface. That grade becomes a score, and your highest score of the week is what the leaderboard ranks.",
  },
  {
    q: "Is the leaderboard private?",
    a: "The board shows display names only. No email addresses, account identifiers or collection contents are ever published, and only signed-in members can view it.",
  },
  {
    q: "When does the leaderboard reset?",
    a: "It is a rolling seven-day window in UTC, so the oldest day drops off at 00:00 UTC each night rather than resetting to empty.",
  },
];

export const Route = createFileRoute("/leaderboard")({
  head: () => ({
    meta: [
      { title: "Palworld Card Game Weekly Leaderboard — Pal Stars" },
      {
        name: "description",
        content:
          "The Pal Stars weekly leaderboard: best graded Palworld card pulls, Gem Mint 10s, Palsino jackpots and daily Pal Puzzle solves from the last seven days.",
      },
      { property: "og:title", content: "Palworld weekly leaderboard — best pulls and jackpots" },
      {
        property: "og:description",
        content: "See the week's best Palworld TCG pulls, Gem Mint 10s, Palsino jackpots and Pal Puzzle streaks on Pal Stars.",
      },
      { property: "og:url", content: "https://palstars.com/leaderboard" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/leaderboard" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Weekly Leaderboard", "/leaderboard"]]),
      faqSchema(FAQ, "https://palstars.com/leaderboard"),
    ],
  }),
  component: LeaderboardPage,
});

function LeaderboardPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars competition</p>
        <h1 className="mt-2 text-3xl font-bold">Palworld weekly leaderboard</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          This is the Pal Stars weekly leaderboard: the best graded Palworld card pulls, Gem Mint 10s, packs opened,
          Palsino jackpots and daily Pal Puzzle solves of the last seven UTC days. It updates as you play, shows display
          names only, and rolls forward one day at a time instead of resetting to empty.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/pack-opening" className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-110">
            Open packs and climb the board
          </Link>
          <Link to="/daily-puzzle" className="rounded-md border px-5 py-2.5 text-sm font-semibold hover:bg-card">
            Play today's Pal Puzzle
          </Link>
          <Link to="/palsino" className="rounded-md border px-5 py-2.5 text-sm font-semibold hover:bg-card">
            Spin Palsino
          </Link>
        </div>
      </header>

      <section aria-labelledby="board-heading">
        <h2 id="board-heading" className="text-xl font-bold">This week's top 25</h2>
        <div className="mt-4">
          <Suspense fallback={<p className="text-sm text-muted-foreground">Loading the leaderboard…</p>}>
            <WeeklyLeaderboard />
          </Suspense>
        </div>
      </section>

      <FaqSection items={FAQ} heading="Palworld leaderboard FAQ" />
    </div>
  );
}
